import { normalizeId } from '@studio/models/identifier';
import type { Room } from '@studio/models/room';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { shallow } from 'zustand/shallow';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  BillingRegistryTrialNoticeKind as Kind,
  BillingRegistryTrialNoticeRequest,
  BillingRegistryPlanPurchaseState as PurchaseState,
  type BillingRegistryTrialNotice
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { BillingRegistryTrialNotice as NoticeService } from '@tryiris-ai/mailroom-helper/billing';
import { getActiveStudioId, subscribeActiveStudio } from '#~/analytics/active-studio.js';
import {
  getTrialNoticeBillingRead,
  subscribeTrialNoticeBilling,
  forceRefreshBilling
} from '#~/api/billing/limits.js';
import {
  StandardTrialNoticeController,
  type DisplayedTrialNotice
} from '#~/api/billing/trial-notice-controller.js';
import { usePlanPurchases } from '#~/api/billing/use-plan-purchase.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { useDepot } from '#~/depot/hooks.js';
import { nats } from '#~/nats/index.js';
import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useOnboardingPermissionsClaim } from '#~/onboarding/store.js';
import { useInitialPermissionsCheck } from '#~/routes/electron/permissions/prompt-gate.js';
import { useSettings } from '../../hooks.js';
import { Panel } from '../types.js';
import { TrialWelcome } from './trial-welcome.js';

// An absent dialog is not first-run completion: status queries can still be unresolved.
// Read live stores again before publishing an asynchronously authorized welcome.
// Per knowledge: studio.client/trial-welcome-first-run-permission-gap (ai.knowledge).
const initialPermissionsReady = (): boolean =>
  !useOnboardingPermissionsClaim.getState().funnelOwnsPermissions &&
  (!isElectronApp() ||
    config.noMediaInputSandbox === true ||
    useInitialPermissionsCheck.getState().complete);
const subscribeInitialPermissions = (listener: () => void) => {
  const removeCheck = useInitialPermissionsCheck.subscribe(listener);
  const removeClaim = useOnboardingPermissionsClaim.subscribe(listener);
  return () => {
    removeCheck();
    removeClaim();
  };
};

const canPresent = (): boolean =>
  document.visibilityState === 'visible' &&
  document.querySelector('dialog[open], [role="dialog"]:not([data-trial-welcome])') === null;
const completeSummary = (notice: BillingRegistryTrialNotice): boolean =>
  notice.activeTrial === true &&
  notice.trialId !== '' &&
  notice.trialEnd > 0 &&
  notice.serverNowMs > 0 &&
  notice.trialPlan !== undefined &&
  notice.trialPlan.title.trim() !== '' &&
  notice.nextPlan !== undefined &&
  notice.nextPlan.title.trim() !== '';

/** Welcome presentation remains independent from enrollment and existing Billing navigation. */
export function TrialWelcomeRoot() {
  const roomId = useSyncExternalStore(subscribeActiveStudio, getActiveStudioId);
  const studio = normalizeId<Room['id']>(roomId);
  return studio === undefined ? null : <TrialWelcomeStudio key={studio} roomId={studio} />;
}

function TrialWelcomeStudio({ roomId }: { readonly roomId: Room['id'] }) {
  const permissionsReady = useSyncExternalStore(
    subscribeInitialPermissions,
    initialPermissionsReady
  );
  const auth = useAuth0();
  const { actorKey, isCurrentAccount, purchases } = usePlanPurchases();
  const settings = useSettings();
  const { role, ready } = useDepot(
    (depot) => ({
      role: depot.roomRecords[roomId]?.permissions.role,
      ready:
        depot.user?.id !== undefined &&
        depot.boot.observation?.accountId === depot.user.id &&
        depot.boot.observation.roomId === roomId &&
        depot.boot.observation.outcome === 'ready'
    }),
    shallow
  );
  const [billing, setBilling] = useState(getTrialNoticeBillingRead);
  const [available, setAvailable] = useState(canPresent);
  const [view, setView] = useState<DisplayedTrialNotice | null>(null);
  const [summary, setSummary] = useState<DisplayedTrialNotice | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const displayed = useRef<DisplayedTrialNotice | null>(null);
  const focusOrigin = useRef<HTMLElement | null>(null);
  const latest = useRef({
    actorKey,
    roomId,
    isCurrentAccount,
    getToken: auth.getAccessTokenSilently
  });
  latest.current = { actorKey, roomId, isCurrentAccount, getToken: auth.getAccessTokenSilently };
  const [controller] = useState(
    () =>
      new StandardTrialNoticeController({
        observationKind: Kind.TRIAL_NOTICE_WELCOME,
        request: async (studio, operation, kind, commandId, displayId) => {
          const account = latest.current;
          const id = toIdFromHex(studio);
          if (id === undefined || id === null || !account.isCurrentAccount())
            throw new Error('Welcome account changed');
          const token = await account.getToken();
          if (!account.isCurrentAccount()) throw new Error('Welcome account changed');
          const response = await NoticeService.request(
            nats.cloudConnection(),
            BillingRegistryTrialNoticeRequest.fromPartial({
              roomID: id,
              token,
              operation,
              commandId,
              ...(kind === undefined ? {} : { kind }),
              ...(displayId === undefined ? {} : { displayId })
            })
          );
          if (!account.isCurrentAccount()) throw new Error('Welcome account changed');
          return response;
        },
        canDisplay: () => initialPermissionsReady() && canPresent(),
        uuid: () => crypto.randomUUID(),
        monotonicNow: () => performance.now(),
        publish: (next) => {
          // An automatic welcome returns focus to the Studio control that preceded it.
          if (next !== null && displayed.current === null)
            focusOrigin.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          displayed.current = next;
          setView(next);
        },
        observe: (notice, receivedAt) => {
          const current = latest.current;
          if (
            notice?.shownRecorded === true &&
            displayed.current === null &&
            current.isCurrentAccount()
          )
            setDismissed(true);
          setSummary(
            notice !== null &&
              completeSummary(notice) &&
              current.actorKey !== undefined &&
              current.isCurrentAccount()
              ? { notice, receivedAt, roomID: current.roomId, actorID: current.actorKey }
              : null
          );
        }
      })
  );
  useEffect(() => subscribeTrialNoticeBilling(setBilling), []);
  useLayoutEffect(() => {
    setDismissed(false);
    setSummary(null);
  }, [actorKey, roomId]);
  useLayoutEffect(() => {
    controller.activate();
    return () => controller.dispose();
  }, [controller]);
  useLayoutEffect(() => {
    if (
      purchases[roomId]?.receiptVerified === true &&
      purchases[roomId]?.receipt?.state === PurchaseState.PURCHASE_STATE_CONFIRMED
    )
      controller.confirmedPaid(roomId);
  }, [controller, purchases, roomId]);
  useLayoutEffect(() => {
    void controller.setContext({
      roomID: roomId,
      actorID: actorKey ?? null,
      role,
      // After dismissal, refresh may retry the shown receipt but cannot keep polling catalog data.
      ready: ready && permissionsReady && available && !dismissed && isCurrentAccount(),
      billing
    });
  }, [
    controller,
    roomId,
    actorKey,
    role,
    ready,
    permissionsReady,
    available,
    dismissed,
    billing,
    isCurrentAccount
  ]);
  useEffect(() => {
    const refresh = () => {
      setAvailable(canPresent());
      if (document.visibilityState === 'visible') {
        void forceRefreshBilling(roomId);
        void controller.refresh();
      }
    };
    const observer = new MutationObserver(() => setAvailable(canPresent()));
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['role', 'open']
    });
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') void controller.refresh();
    }, 5000);
    return () => {
      observer.disconnect();
      clearInterval(poll);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [controller, roomId]);
  useEffect(() => {
    if (summary === null) return;
    const remaining =
      summary.notice.trialEnd * 1000 -
      summary.notice.serverNowMs -
      (performance.now() - summary.receivedAt);
    // Expiry retains its original server deadline even if a later read fails.
    const deadline = setTimeout(
      () => {
        setSummary(null);
        controller.dismiss();
        setDismissed(true);
        void forceRefreshBilling(roomId);
      },
      Math.min(Math.max(0, remaining), 2147483647)
    );
    return () => {
      clearTimeout(deadline);
    };
  }, [controller, roomId, summary]);
  const dismiss = useCallback(() => {
    controller.dismiss();
    setDismissed(true);
  }, [controller]);
  const confirmed =
    summary !== null &&
    summary.roomID === roomId &&
    summary.actorID === actorKey &&
    isCurrentAccount() &&
    billing.roomID === roomId &&
    billing.status === 'loaded' &&
    billing.response?.billingStateKnown === true &&
    billing.response.trialEnd === summary.notice.trialEnd;
  const seconds =
    summary === null
      ? 0
      : (summary.notice.trialEnd * 1000 -
          summary.notice.serverNowMs -
          (performance.now() - summary.receivedAt)) /
        1000;
  const canViewDetails =
    confirmed &&
    summary.notice.canViewPlanDetails &&
    (role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER ||
      role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN);
  const details = () => {
    if (!canViewDetails) return;
    dismiss();
    if (role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER)
      settings(Panel.Billing, roomId, { openPlanPicker: true });
    else settings(Panel.Billing, roomId);
  };
  // Render-time identity checks prevent a changed account or Studio from painting an older response.
  if (
    !confirmed ||
    summary === null ||
    !ready ||
    seconds <= 0 ||
    role === undefined ||
    role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED
  )
    return null;
  const notice = summary.notice;
  const open =
    permissionsReady &&
    available &&
    !dismissed &&
    notice.eligible &&
    view?.actorID === actorKey &&
    view.roomID === roomId;
  if (!open) return null;
  return (
    <TrialWelcome
      key={view.notice.displayId}
      roomId={roomId}
      notice={notice}
      onDismiss={dismiss}
      onShown={() => {
        if (view.notice.displayId !== '') controller.reportShown(view.notice.displayId);
      }}
      {...(canViewDetails ? { onPlanDetails: details } : {})}
      onReturnFocus={() => {
        // Without a trigger button, use the previous control or the first available Studio control.
        const previous = focusOrigin.current;
        const fallback = [
          ...document.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], [tabindex="0"]'
          )
        ].find((element) => !element.closest('[data-trial-welcome]'));
        (previous?.isConnected && previous !== document.body ? previous : fallback)?.focus();
      }}
    />
  );
}
